import React, { useState, createContext, useEffect } from 'react';
import './index.css';
import './App.css';
import './custom.css';
import './assets/icons/icons.css';
import HomePage from './pages/HomePage';
import FriendsPage from './pages/FriendsPage';
import WalletPage from './pages/WalletPage';
import { UserDataProvider } from './contexts/UserDataContext';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
// Import Appwrite services
import { 
  createUserDocument
} from './services/appwrite';

// Create contexts for modal state and Telegram user that can be accessed across components
export const ModalContext = createContext();
export const TelegramUserContext = createContext();

// Add page caching to prevent remounting on tab switches
const PageContainer = React.memo(({ isActive, children }) => (
  <div style={{ 
    display: isActive ? 'block' : 'none',
    width: '100%',
    height: '100%'
  }}>
    {children}
  </div>
));

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isDailyLoginOpen, setIsDailyLoginOpen] = useState(false);
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const [telegramUser, setTelegramUser] = useState({
    id: null,
    username: 'User',
    firstName: '',
    lastName: '',
    photoUrl: '',
    isVerified: false
  });

  // Use the Telegram WebApp hook for fullscreen and other features
  const { 
    isFullscreen, 
    viewportHeight
  } = useTelegramWebApp();

  // Initialize user registration and Telegram WebApp
  useEffect(() => {
    // Prevent multiple registrations
    if (registrationAttempted) {
      return;
    }

    // Function to register a user in the database
    const registerUser = async (userData) => {
      if (!userData || !userData.id) {
        return;
      }
      
      try {
        // Extract referral code from various sources
        let referralCode = null;
        
        // 1. Check Telegram WebApp data
        if (window.Telegram && window.Telegram.WebApp) {
          const tgWebApp = window.Telegram.WebApp;
          
          // Check for start_parameter
          const startParam = tgWebApp.initDataUnsafe?.start_parameter;
          if (startParam) {
            referralCode = startParam;
          }
          
          // Check initData string for parameters
          if (!referralCode && tgWebApp.initData) {
            try {
              // Parse the raw initData string directly for start_parameter
              const initDataParams = new URLSearchParams(tgWebApp.initData);
              const startappParam = initDataParams.get('start_parameter') || initDataParams.get('startapp');
              
              if (startappParam) {
                referralCode = startappParam;
              }
              
              // Also check if start_parameter is embedded in other way
              if (!referralCode) {
                // Sometimes start_parameter is in the raw string but not as URL param
                const startParamMatch = tgWebApp.initData.match(/start_parameter=([^&]*)/);
                if (startParamMatch && startParamMatch[1]) {
                  referralCode = decodeURIComponent(startParamMatch[1]);
                }
              }
            } catch (error) {
              // Silently handle parsing errors
            }
          }
        }
        
        // 2. Check URL parameters (both current and hash)
        if (!referralCode) {
          try {
            const urlParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            
            // Check for various parameter names that Telegram might use
            const urlRef = urlParams.get('ref') || 
                          urlParams.get('startapp') || 
                          urlParams.get('start_parameter') ||
                          urlParams.get('tgWebAppStartParam') ||
                          hashParams.get('ref') || 
                          hashParams.get('startapp') || 
                          hashParams.get('start_parameter') ||
                          hashParams.get('tgWebAppStartParam');
            
            if (urlRef) {
              referralCode = urlRef;
            }
          } catch (error) {
            // Silently handle parsing errors
          }
        }
        
        // 3. Final check for any tgWebAppStartParam global
        if (!referralCode && window.tgWebAppStartParam) {
          referralCode = window.tgWebAppStartParam;
        }
        
        // Create or verify user document with referral code
        const result = await createUserDocument(userData, referralCode);
        
        if (!result) {
          console.error('Failed to register/verify user');
        }
        
        return result;
      } catch (error) {
        console.error('Error registering user:', error);
        return null;
      }
    };
    
    let userData;
    
    // Handle Telegram WebApp integration
    if (window.Telegram && window.Telegram.WebApp) {
      const tgWebApp = window.Telegram.WebApp;
      tgWebApp.ready();
      
      // Expand to full screen
      try {
        tgWebApp.expand();
      } catch (error) {
        // Silently handle errors
      }
      
      // Set header color to match app (only if supported)
      try {
        const version = tgWebApp.version || '6.0';
        const versionNum = parseFloat(version);
        
        // Only try to set colors if supported in this version
        if (versionNum >= 6.1 && tgWebApp.setHeaderColor) {
          tgWebApp.setHeaderColor('#ffffff');
        }
        
        if (versionNum >= 6.1 && tgWebApp.setBackgroundColor) {
          tgWebApp.setBackgroundColor('#ffffff');
        }
      } catch (error) {
        // Silently handle unsupported features
      }
      
      // Check Telegram WebApp version and disable swipes if supported
      try {
        const version = tgWebApp.version || '6.0';
        const versionNum = parseFloat(version);
        
        // Use newer method if available, fallback to older method for newer versions
        if (versionNum >= 7.0 && tgWebApp.disableVerticalSwipes) {
          tgWebApp.disableVerticalSwipes();
        } else if (versionNum >= 6.2 && tgWebApp.setVerticalSwipeStatus) {
          tgWebApp.setVerticalSwipeStatus(false);
        }
      } catch (error) {
        // Silently handle unsupported features
      }
      
      // Hide default buttons with version checks
      try {
        if (tgWebApp.MainButton && tgWebApp.MainButton.hide) {
          tgWebApp.MainButton.hide();
        }
        // Only try to hide BackButton if supported
        if (parseFloat(tgWebApp.version || '6.0') > 6.0 && tgWebApp.BackButton && tgWebApp.BackButton.hide) {
          tgWebApp.BackButton.hide();
        }
      } catch (error) {
        // Silently handle unsupported features
      }
      
      // Get user data from Telegram WebApp
      const user = tgWebApp.initDataUnsafe?.user || {};
      
      userData = {
        id: user.id || null,
        username: user.username || 'User',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        photoUrl: user.photo_url || '',
        isVerified: !!user.is_verified
      };
    } else {
      // For development/testing only - create a mock user when not in Telegram
      userData = {
        id: '99999',
        username: 'test_user',
        firstName: 'Test',
        lastName: 'User',
        photoUrl: '',
        isVerified: false
      };
    }
    
    // Set the user data in state
    setTelegramUser(userData);
    
    // Register user exactly once and wait for completion
    if (userData && userData.id && !registrationAttempted) {
      setRegistrationAttempted(true);
      
      // Wait for registration to complete before marking as done
      registerUser(userData).then((result) => {
        setIsRegistrationComplete(true);
      });
    }
  }, [registrationAttempted]); // Include registrationAttempted as dependency

  return (
    <TonConnectUIProvider manifestUrl={`${window.location.origin}/tonconnect-manifest.json`}>
      <TelegramUserContext.Provider value={telegramUser}>
        <UserDataProvider>
          <ModalContext.Provider value={{ isDailyLoginOpen, setIsDailyLoginOpen }}>
            <div className={`App app-container telegram-mini-app ${
              isDailyLoginOpen ? 'modal-open' : ''
            } ${
              isFullscreen ? 'fullscreen' : ''
            } ${
              viewportHeight > 0 ? 'has-viewport-height' : ''
            }`}>
                {/* Keep all pages mounted but only show the active one */}
                <PageContainer isActive={currentPage === 'home'}>
                  <HomePage onNavigate={setCurrentPage} isRegistrationComplete={isRegistrationComplete} />
                </PageContainer>
                
                <PageContainer isActive={currentPage === 'friends'}>
                  <FriendsPage onNavigate={setCurrentPage} />
                </PageContainer>
                
                <PageContainer isActive={currentPage === 'wallet'}>
                  <WalletPage onNavigate={setCurrentPage} />
                </PageContainer>

              </div>
          </ModalContext.Provider>
        </UserDataProvider>
      </TelegramUserContext.Provider>
    </TonConnectUIProvider>
  );
}

export default App;
